import React, { useState, useEffect, useMemo } from 'react';
import { escapeHtml } from '../utils/sanitize';
import { format, isBefore, startOfDay, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Skeleton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import { Add, Edit, Delete, CalendarMonth, ViewList, Visibility, Clear, Refresh, Download, CheckCircle, Receipt, Assessment, AccessTime } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { bookingApi, propertyApi, unitApi, userApi, Booking, documentsApi, dailyRatesApi, brokerApi } from '../services/api';
import { formatDate } from '../utils/formatters';
import BookingCalendar from '../components/BookingCalendar';
import CountryCodeSelector from '../components/CountryCodeSelector';
import HierarchicalUnitSelector from '../components/HierarchicalUnitSelector';
import { getUserCountry, getCountryPhoneCode } from '../data/countries';
import { useLanguage } from '../contexts/LanguageContext';
import { useSections } from '../contexts/SectionsContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getStorageUrl, getApiUrl } from '../config/api';

const BookingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [originalBookingData, setOriginalBookingData] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [viewingBooking, setViewingBooking] = useState<Booking | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [ticketBooking, setTicketBooking] = useState<Booking | null>(null);
  const [ticketDocuments, setTicketDocuments] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [bookingHistory, setBookingHistory] = useState<any[]>([]);
  
  // Invoice dialog states
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [selectedBookingForInvoice, setSelectedBookingForInvoice] = useState<any>(null);
  const [invoiceType, setInvoiceType] = useState<'summary' | 'details'>('summary');
  
  // Filter states
  const [filters, setFilters] = useState({
    bookingNumber: '',
    tenantName: '',
    tenantPhone: '',
    property: '',
    unit: '',
    createdBy: '',
    fromDate: '',
    toDate: '',
    status: ''
  });

  // Setting to allow past booking creation (persisted in localStorage)
  const [allowPastBookings, setAllowPastBookings] = useState(() => {
    try {
      const saved = localStorage.getItem('allowPastBookings');
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.warn('Failed to read allowPastBookings from localStorage:', error);
      return false;
    }
  });

  // Get sections state from context
  const { showSectionsAboveCalendar } = useSections();
  
  // Get sidebar state from context
  const { sidebarCollapsed, drawerWidth } = useSidebar();

  // Handler to toggle past bookings setting and persist it
  const handleTogglePastBookings = (checked: boolean) => {
    setAllowPastBookings(checked);
    try {
      localStorage.setItem('allowPastBookings', JSON.stringify(checked));
    } catch (error) {
      console.warn('Failed to save allowPastBookings to localStorage:', error);
    }
  };

  // Note: Unit filter is cleared when property filter changes (handled in property onChange)

  const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      property_id: '',
      unit_id: '',
      broker_id: '',
      tenant_name: '',
      tenant_email: '',
      tenant_phone: '',
      country_code: getCountryPhoneCode(getUserCountry()),
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
      nights: 1,
      rent_per_night: 0,
      rent_amount: 0,
      extra_amount: 0,
      extra_description: '',
      deposit_amount: 0,
      status: 'active',
      cancel_after_minutes: 180, // Default to 3 hours
      commission_rate: 0,
      commission_amount: 0,
      notes: '',
    },
    mode: 'onChange', // Enable real-time validation
  });

  const watchPropertyId = watch('property_id');
  const watchStartDate = watch('start_date');
  const watchNights = watch('nights');
  const watchRentPerNight = watch('rent_per_night');
  const watchStatus = watch('status');
  const watchBrokerId = watch('broker_id');
  const watchRentAmount = watch('rent_amount');
  const watchExtraAmount = watch('extra_amount');

  // Auto-cancel duration options
  const cancelAfterOptions = [
    { value: 3, label: '3 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 180, label: '3 hours' },
    { value: 1440, label: '1 day' },
    { value: 4320, label: '3 days' },
    { value: 14400, label: '10 days' },
  ];

  // Helper function to get property currency symbol or fallback to main currency
  const getPropertyCurrencySymbol = () => {
    const selectedPropertyId = watchPropertyId;
    
    // When editing a booking, try to use the booking's own currency first
    if (editingBooking?.currency && Array.isArray(currencies)) {
      const bookingCurrency = currencies.find((c: any) => c.id === editingBooking.currency?.id);
      if (bookingCurrency) {
        return bookingCurrency.symbol;
      }
    }
    
    // Fallback to booking's property currency when editing
    if (editingBooking?.property?.currency_id && Array.isArray(currencies)) {
      const bookingPropertyCurrency = currencies.find((c: any) => c.id === editingBooking.property?.currency_id);
      if (bookingPropertyCurrency) {
        return bookingPropertyCurrency.symbol;
      }
    }
    
    // If no property is selected, use main currency
    if (!selectedPropertyId) {
      return getMainCurrencySymbol();
    }
    
    // Find the selected property
    const selectedProperty = Array.isArray(properties) 
      ? (properties as any[]).find((p: any) => p.id === parseInt(selectedPropertyId))
      : null;
    
    // If property has a currency_id, find the currency
    if (selectedProperty?.currency_id && Array.isArray(currencies)) {
      const propertyCurrency = currencies.find((c: any) => c.id === selectedProperty.currency_id);
      if (propertyCurrency) {
        return propertyCurrency.symbol;
      }
    }
    
    // Fallback to main currency
    return getMainCurrencySymbol();
  };

  // Helper function to get main currency symbol (default currency)
  const getMainCurrencySymbol = () => {
    if (!Array.isArray(currencies) || currencies.length === 0) {
      return '$';
    }
    const defaultCurrency = currencies.find((c: any) => c.is_default);
    return defaultCurrency?.symbol || '$';
  };

  // Helper function to format amount with currency symbol
  const formatAmountWithCurrency = (booking: any, amountType: string) => {
    if (!booking.display_amounts) {
      // Fallback for old bookings without display_amounts
      const amount = booking[amountType] || 0;
      const symbol = booking.currency?.symbol || '$';
      return `${symbol}${Number(amount).toLocaleString()}`;
    }
    
    const displayAmount = booking.display_amounts[amountType];
    if (!displayAmount) return '$0';
    
    const symbol = displayAmount.symbol || '$';
    const value = displayAmount.value || 0;
    return `${symbol}${Number(value).toLocaleString()}`;
  };

  // Helper function to format remaining amount
  const formatRemainingAmount = (booking: any) => {
    if (!booking.display_amounts) {
      // Fallback for old bookings
      const total = Number(booking.total_amount || 0);
      const paid = Number(booking.paid_amount || 0);
      const remaining = Math.max(0, total - paid);
      const symbol = booking.currency?.symbol || '$';
      return `${symbol}${remaining.toLocaleString()}`;
    }
    
    const totalDisplay = booking.display_amounts.total_amount;
    const paidDisplay = booking.display_amounts.paid_amount;
    
    if (!totalDisplay || !paidDisplay) return '$0';
    
    const totalValue = Number(totalDisplay.value || 0);
    const paidValue = Number(paidDisplay.value || 0);
    
    // For remaining amount, we need to consider mixed currencies
    // Total is in default currency, paid is in booking currency
    // We'll show remaining in default currency for consistency
    const remaining = Math.max(0, totalValue - paidValue);
    const symbol = totalDisplay.symbol || '$';
    return `${symbol}${remaining.toLocaleString()}`;
  };

  // Helper function to get hierarchical unit name by unit ID for booking details
  const getUnitHierarchyNameById = (unitId: number): string => {
    const unit = allUnits?.find((u: any) => u.id === unitId);
    if (!unit) return 'Unknown Unit';
    
    // Build parent path using units array (since unit.parent might not be populated)
    const buildParentPath = (currentUnit: any): string => {
      const parent = allUnits?.find((u: any) => u.id === currentUnit.parent_unit_id);
      if (!parent) return currentUnit.unit_number;
      
      const parentPath = buildParentPath(parent);
      return `${parentPath} → ${currentUnit.unit_number}`;
    };
    
    return buildParentPath(unit);
  };

  const getFieldDisplayName = (fieldName: string): string => {
    const fieldMapping: { [key: string]: string } = {
      'paid_amount': 'total_paid',
      'total_amount': 'total_amount',
      'payment_status': 'payment_status',
      'start_date': 'start_date',
      'end_date': 'end_date',
      'guest_name': 'guest_name',
      'guest_email': 'guest_email',
      'guest_phone': 'guest_phone',
      'notes': 'notes',
      'status': 'status'
    };
    
    return fieldMapping[fieldName] || fieldName;
  };

  // Effect to handle form setup when editing a booking
  useEffect(() => {
    if (editingBooking && openDialog) {
      console.log('Setting up form for editing booking:', editingBooking);
      
      // Helper function to format dates for input fields
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        
        let date: Date;
        
        if (dateString.includes('T')) {
          date = new Date(dateString);
        } else if (dateString.includes('-')) {
          date = new Date(dateString + 'T00:00:00');
        } else {
          date = new Date(dateString);
        }
        
        if (isNaN(date.getTime())) {
          console.warn('Invalid date string:', dateString);
          return '';
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formatted = `${year}-${month}-${day}`;
        return formatted;
      };

      // Format phone number
      let phoneNumber = '';
      let countryCode = getCountryPhoneCode(getUserCountry());
      
      if (editingBooking.tenant_phone) {
        
        const originalPhone = editingBooking.tenant_phone.trim();
        
        // Check if phone already contains a country code
        const phoneWithCountryCodeMatch = originalPhone.match(/^(\+\d{1,4})\s*(.+)$/);
        
        if (phoneWithCountryCodeMatch) {
          // Phone number has country code - extract it
          const extractedCountryCode = phoneWithCountryCodeMatch[1];
          const extractedPhoneNumber = phoneWithCountryCodeMatch[2];
          
          // Check if the phone number part already contains the country code (duplication case)
          const duplicateCountryCodeMatch = extractedPhoneNumber.match(/^(\+\d{1,4})\s*(.+)$/);
          
          if (duplicateCountryCodeMatch) {
            // There's duplication - use the first country code and the clean phone number
            countryCode = extractedCountryCode;
            phoneNumber = duplicateCountryCodeMatch[2];
          } else {
            // No duplication - use extracted values
            countryCode = extractedCountryCode;
            phoneNumber = extractedPhoneNumber;
          }
        } else {
          // Phone number doesn't start with country code - use as is
          phoneNumber = originalPhone;
        }
        
        // Extra cleanup: remove any remaining + signs and extra spaces from phone number
        phoneNumber = phoneNumber.replace(/^\+/, '').replace(/\s+/g, ' ').trim();
      }

      // Calculate form values
      const startDate = new Date(editingBooking.start_date);
      const endDate = new Date(editingBooking.end_date);
      const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const rentAmount = Number(editingBooking.rent_amount || 0);
      const rentPerNight = nights > 0 && rentAmount > 0 ? rentAmount / nights : 0;
      
      const formattedStartDate = formatDateForInput(editingBooking.start_date);
      const formattedEndDate = formatDateForInput(editingBooking.end_date);
      
      const formData = {
        property_id: editingBooking.property_id?.toString() || '',
        unit_id: editingBooking.unit_id?.toString() || '',
        broker_id: editingBooking.broker_id?.toString() || '',
        commission_rate: editingBooking.commission_rate || 0,
        commission_amount: editingBooking.commission_amount || 0,
        tenant_name: editingBooking.tenant_name,
        tenant_email: editingBooking.tenant_email || '',
        tenant_phone: phoneNumber,
        country_code: countryCode,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        nights: nights > 0 ? nights : 1,
        rent_per_night: parseFloat((rentPerNight || 0).toFixed(2)),
        rent_amount: editingBooking.rent_amount,
        extra_amount: editingBooking.extra_amount || 0,
        extra_description: editingBooking.extra_description || '',
        deposit_amount: editingBooking.deposit_amount,
        currency_id: editingBooking.currency_id?.toString() || '',
        status: editingBooking.status,
        cancel_after_minutes: editingBooking.cancel_after_minutes || 180,
        notes: editingBooking.notes || '',
      };
      
      console.log('Editing booking - original data:', editingBooking);
      console.log('Form data to be set:', formData);
      
      // Store original data for change detection
      setOriginalBookingData(formData);
      
      // Reset form with all values
      reset(formData);
      
      // Also explicitly set the phone-related values to ensure they're updated
      setTimeout(() => {
        setValue('tenant_phone', phoneNumber, { shouldValidate: true });
        setValue('country_code', countryCode, { shouldValidate: true });
        setValue('start_date', formattedStartDate, { shouldValidate: true });
        setValue('end_date', formattedEndDate, { shouldValidate: true });
        console.log('Form values updated after timeout');
      }, 100);
    }
  }, [editingBooking, openDialog, reset, setValue]);

  // Helper function to calculate nights from dates
  const calculateNights = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  };

  // Helper function to calculate end date from start date and nights
  const calculateEndDate = (startDate: string, nights: number): string => {
    if (!startDate || nights <= 0) return '';
    const start = new Date(startDate);
    const end = new Date(start.getTime() + (nights * 24 * 60 * 60 * 1000));
    return end.toISOString().split('T')[0];
  };

  // Helper function to calculate total rent amount
  const calculateTotalRent = (rentPerNight: number, nights: number): number => {
    if (!rentPerNight || !nights || rentPerNight <= 0 || nights <= 0) return 0;
    return rentPerNight * nights;
  };

  // Helper function to calculate rent per night from total rent
  const calculateRentPerNight = (totalRent: number, nights: number): number => {
    if (!totalRent || !nights || totalRent <= 0 || nights <= 0) return 0;
    return parseFloat((totalRent / nights).toFixed(2));
  };

  // Fetch all bookings
  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['bookings'],
    queryFn: bookingApi.getAll,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Debug logging
  React.useEffect(() => {
    console.log('Bookings data updated:', bookings);
    console.log('Bookings loading:', bookingsLoading);
  }, [bookings, bookingsLoading]);

  // Fetch properties for dropdown
  const { data: properties = [] } = useQuery({
    queryKey: ['properties'],
    queryFn: propertyApi.getAll,
  });

  // Fetch all units for calendar display
  const { data: allUnits = [] } = useQuery({
    queryKey: ['units-all'],
    queryFn: unitApi.getAll,
  });

  // Fetch users for created by filter
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: userApi.getAll,
  });

  // Fetch currencies for currency selection
  const { data: currencies = [], isLoading: isLoadingCurrencies, error: currenciesError } = useQuery({
    queryKey: ['currencies'],
    queryFn: async () => {
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      // Ensure we always return an array
      const currenciesArray = Array.isArray(data) ? data : (data.data || []);
      return currenciesArray;
    },
    retry: 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch brokers for broker selection
  const { data: brokersResponse } = useQuery({
    queryKey: ['brokers'],
    queryFn: brokerApi.getAll,
  });
  
  const brokers = brokersResponse?.data || [];

  // Note: HierarchicalUnitSelector handles unit filtering internally using allUnits

  // Auto-populate commission fields when broker is selected
  useEffect(() => {
    if (watchBrokerId && brokers.length > 0) {
      const selectedBroker = brokers.find((broker: any) => broker.id.toString() === watchBrokerId);
      if (selectedBroker && selectedBroker.commission_rate > 0) {
        const rentAmount = Number(watchRentAmount) || 0;
        const extraAmount = Number(watchExtraAmount) || 0;
        const totalAmount = rentAmount + extraAmount;
        const commissionRate = selectedBroker.commission_rate;
        const commissionAmount = (totalAmount * commissionRate) / 100;
        
        // Only auto-populate if commission rate is not already set
        const currentCommissionRate = watch('commission_rate');
        if (!currentCommissionRate || currentCommissionRate === 0) {
          setValue('commission_rate', commissionRate);
          setValue('commission_amount', commissionAmount);
          
          // Move focus to commission rate field after auto-population
          setTimeout(() => {
            const commissionRateInput = document.querySelector('input[name="commission_rate"]') as HTMLInputElement;
            if (commissionRateInput) {
              commissionRateInput.focus();
              commissionRateInput.select(); // Select the text so user can easily modify it
            }
          }, 100);
        }
      }
    }
  }, [watchBrokerId, watchRentAmount, watchExtraAmount, brokers, setValue, watch]);

  // Create booking mutation
  const createBookingMutation = useMutation({
    mutationFn: bookingApi.create,
    onSuccess: (data) => {
      console.log('Booking created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setSnackbar({ open: true, message: 'Booking created successfully', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error: any) => {
      console.error('Create booking error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to create booking';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    },
  });

  // Update booking mutation
  const updateBookingMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => bookingApi.update(id, data),
    onSuccess: (data) => {
      console.log('Booking updated successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setSnackbar({ open: true, message: 'Booking updated successfully', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error: any) => {
      console.error('Update booking error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to update booking';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    },
  });

  // Delete booking mutation
  const deleteBookingMutation = useMutation({
    mutationFn: bookingApi.delete,
    onSuccess: () => {
      console.log('Booking deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setSnackbar({ open: true, message: 'Booking deleted successfully', severity: 'success' });
    },
    onError: (error: any) => {
      console.error('Delete booking error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to delete booking';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    },
  });

  // Confirm booking mutation
  const confirmBookingMutation = useMutation({
    mutationFn: bookingApi.confirm,
    onSuccess: (data, variables) => {
      console.log('Booking confirmed successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      const bookingNumber = data.booking?.booking_number || data.booking?.id || variables;
      setSnackbar({ open: true, message: `Booking #${bookingNumber} confirmed successfully`, severity: 'success' });
    },
    onError: (error: any) => {
      console.error('Confirm booking error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to confirm booking';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    },
  });

  // Helper function to normalize values for comparison
  const normalizeValue = (value: any): any => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Trim whitespace
      const trimmed = value.trim();
      // Convert numeric strings to numbers for comparison
      if (!isNaN(Number(trimmed)) && trimmed !== '') {
        return Number(trimmed);
      }
      return trimmed;
    }
    return value;
  };

  // Helper function to get only changed fields
  const getChangedFields = (currentData: any, originalData: any): any => {
    if (!originalData) return currentData; // If no original data, send all fields
    
    const changes: any = {};
    let hasChanges = false;
    
    // Fields to compare
    const fieldsToCompare = [
      'property_id', 'unit_id', 'broker_id', 'tenant_name', 'tenant_email', 'tenant_phone',
      'start_date', 'end_date', 'nights', 'rent_per_night', 'rent_amount',
      'extra_amount', 'extra_description', 'deposit_amount', 'currency_id', 'status', 'notes', 'commission_rate', 'commission_amount'
    ];
    
    fieldsToCompare.forEach(field => {
          // Special handling for property_id, unit_id, and broker_id to ensure proper comparison
    if (field === 'property_id' || field === 'unit_id' || field === 'broker_id') {
        const currentValue = parseInt(currentData[field]) || 0;
        const originalValue = parseInt(originalData[field]) || 0;
        
        console.log(`🔍 Checking ${field}:`, {
          currentData: currentData[field],
          originalData: originalData[field],
          currentParsed: currentValue,
          originalParsed: originalValue,
          areEqual: currentValue === originalValue
        });
        
        if (currentValue !== originalValue) {
          changes[field] = currentValue;
          hasChanges = true;
          console.log(`✅ ${field} changed: ${originalValue} -> ${currentValue}`);
        } else {
          console.log(`❌ ${field} unchanged: ${currentValue}`);
        }
      }
      // Special handling for phone number
      else if (field === 'tenant_phone') {
        // Construct current phone number properly
        const currentPhoneNumber = currentData.tenant_phone?.trim() || '';
        const currentCountryCode = currentData.country_code?.trim() || '';
        
        let currentFullPhone = '';
        if (currentPhoneNumber) {
          if (currentPhoneNumber.startsWith(currentCountryCode) || currentPhoneNumber.startsWith('+')) {
            currentFullPhone = currentPhoneNumber;
          } else {
            currentFullPhone = currentCountryCode ? `${currentCountryCode} ${currentPhoneNumber}` : currentPhoneNumber;
          }
        }
        
        // Construct original phone number properly
        const originalPhoneNumber = originalData.tenant_phone?.trim() || '';
        const originalCountryCode = originalData.country_code?.trim() || '';
        
        let originalFullPhone = '';
        if (originalPhoneNumber) {
          if (originalPhoneNumber.startsWith(originalCountryCode) || originalPhoneNumber.startsWith('+')) {
            originalFullPhone = originalPhoneNumber;
          } else {
            originalFullPhone = originalCountryCode ? `${originalCountryCode} ${originalPhoneNumber}` : originalPhoneNumber;
          }
        }
        
        if (currentFullPhone !== originalFullPhone) {
          changes.tenant_phone = currentFullPhone;
          hasChanges = true;
        }
      }
      // Default handling for other fields
      else {
        const currentValue = normalizeValue(currentData[field]);
        const originalValue = normalizeValue(originalData[field]);
        
        if (currentValue !== originalValue) {
          changes[field] = currentValue;
          hasChanges = true;
        }
      }
    });
  
  console.log('Change detection completed:', {
    hasChanges,
    changes,
    currentData,
    originalData
  });
  
  return hasChanges ? changes : null;
  };

  const onSubmitBooking = (data: any) => {
    console.log('=== BOOKING SUBMISSION DEBUG ===');
    console.log('Submitting booking data:', data);
    console.log('Current editing booking:', editingBooking);
    console.log('Original booking data stored:', originalBookingData);
    
    // Convert string values to numbers for the API
    const submitData = {
      ...data,
      property_id: parseInt(data.property_id),
      unit_id: parseInt(data.unit_id),
      broker_id: data.broker_id ? parseInt(data.broker_id) : null,
      commission_rate: parseFloat(data.commission_rate) || 0,
      commission_amount: parseFloat(data.commission_amount) || 0,
      rent_per_night: parseFloat(data.rent_per_night) || 0,
      rent_amount: parseFloat(data.rent_amount) || 0,
      extra_amount: parseFloat(data.extra_amount) || 0,
      deposit_amount: parseFloat(data.deposit_amount) || 0,
      // Handle phone number properly - avoid duplication
      tenant_phone: (() => {
        const phone = data.tenant_phone?.trim() || '';
        const countryCode = data.country_code?.trim() || '';
        
        // If phone already starts with country code, return as is
        if (phone.startsWith(countryCode)) {
          return phone;
        }
        
        // If phone already starts with +, return as is
        if (phone.startsWith('+')) {
          return phone;
        }
        
        // Otherwise combine country code with phone
        return countryCode && phone ? `${countryCode} ${phone}` : phone;
      })(),
    };

    console.log('Processed submit data:', submitData);


    if (editingBooking) {
      // Get only changed fields for update
      const changedFields = getChangedFields(submitData, originalBookingData);
      
      console.log('=== CHANGE DETECTION RESULT ===');
      console.log('Changed fields detected:', changedFields);
      
      if (!changedFields) {
        console.log('No changes detected - closing dialog');
        setSnackbar({ open: true, message: 'No changes to save', severity: 'success' });
        handleCloseDialog();
        return;
      }
      
      console.log('Submitting changes to API:', changedFields);
      updateBookingMutation.mutate({ id: editingBooking.id, data: changedFields });
    } else {
      createBookingMutation.mutate(submitData);
    }
  };

  const handleEdit = (booking: any) => {
    console.log('Editing booking:', booking);
    setEditingBooking(booking);
    setOpenDialog(true);
  };

  const handleDelete = (id: number) => {
    console.log('Attempting to delete booking with ID:', id);
    if (window.confirm('Are you sure you want to delete this booking?')) {
      deleteBookingMutation.mutate(id);
    }
  };

  const handleConfirm = (booking: any) => {
    console.log('Confirming booking:', booking);
    confirmBookingMutation.mutate(booking.id);
  };

  const handleCloseDialog = () => {
    console.log('Closing booking dialog');
    setOpenDialog(false);
    setEditingBooking(null);
    setOriginalBookingData(null);
    reset();
  };

  const handleView = async (booking: any) => {
    console.log('Viewing booking:', booking);
    setOpenViewDialog(true);
    
    // Fetch fresh booking data to ensure we have the latest information including auto_cancel_at
    try {
      const freshBookingData = await bookingApi.getById(booking.id);
      console.log('Fresh booking data:', freshBookingData);
      setViewingBooking(freshBookingData);
    } catch (error) {
      console.error('Failed to fetch fresh booking data:', error);
      // Fallback to the existing booking data
      setViewingBooking(booking);
    }
    
    // Load booking history
    setLoadingHistory(true);
    try {
      console.log('Loading booking history for booking ID:', booking.id);
      const history = await bookingApi.getHistory(booking.id);
      console.log('Booking history loaded:', history);
      setBookingHistory(history);
    } catch (error) {
      console.error('Failed to load booking history:', error);
      setBookingHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingBooking(null);
    setBookingHistory([]);
  };

  const handleInvoice = (booking: any) => {
    console.log('Opening invoice dialog for booking:', booking);
    setSelectedBookingForInvoice(booking);
    setInvoiceType('summary');
    setOpenInvoiceDialog(true);
  };

  const handleCloseInvoiceDialog = () => {
    setOpenInvoiceDialog(false);
    setSelectedBookingForInvoice(null);
    setInvoiceType('summary');
  };

  const handleGenerateInvoice = async () => {
    if (!selectedBookingForInvoice) return;

    console.log('Generating invoice for booking:', selectedBookingForInvoice);
    console.log('Invoice type:', invoiceType);

    let paymentHistory: any[] = [];
    
    // Fetch payment history if Financial Details is selected
    if (invoiceType === 'details') {
      try {
        const history = await bookingApi.getHistory(selectedBookingForInvoice.id);
        paymentHistory = history.filter((h: any) => 
          h.description && h.description.toLowerCase().includes('payment')
        );
        console.log('Fetched payment history:', paymentHistory);
      } catch (error) {
        console.error('Failed to fetch payment history:', error);
        paymentHistory = [];
      }
    }

    // Create payment details section
    let paymentDetailsSection = '';
    if (invoiceType === 'details') {
      paymentDetailsSection = `
      
      📋 Payment Details / تفاصيل المدفوعات:
      `;
      
      // Add deposit amount if it exists
      const depositAmount = selectedBookingForInvoice.deposit_amount;
      if (depositAmount && depositAmount > 0) {
        paymentDetailsSection += `
      • Deposit Amount / المبلغ المدفوع عند الحجز:
        ${depositAmount} ${selectedBookingForInvoice.currency?.symbol || 'OMR'}`;
      }
      
      // Add payment history if available
      if (paymentHistory.length > 0) {
        paymentDetailsSection += `
      • Payment History / تاريخ المدفوعات:`;
        
        paymentHistory.forEach((payment: any, index: number) => {
          const paymentDate = new Date(payment.created_at).toLocaleDateString();
          const paymentAmount = payment.description.match(/Payment (?:of )?(\d+\.?\d*)/i);
          const amount = paymentAmount ? paymentAmount[1] : 'N/A';
          
          paymentDetailsSection += `
        ${index + 1}. ${amount} ${selectedBookingForInvoice.currency?.symbol || 'OMR'} - ${paymentDate}`;
        });
      }
      
      paymentDetailsSection += `
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
    }
    
    // Create invoice content
    const propertyLogo = selectedBookingForInvoice.property?.logo_url 
      ? getStorageUrl(selectedBookingForInvoice.property.logo_url)
      : null;
    
    const invoiceContent = `
      📋 INVOICE / فاتورة
      
      Invoice Number: INV-${selectedBookingForInvoice.booking_number || selectedBookingForInvoice.id}
      Date: ${new Date().toLocaleDateString()}
      
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      
      🏢 Property Details / تفاصيل العقار:
      Property: ${selectedBookingForInvoice.property?.name}
      Unit: ${selectedBookingForInvoice.unit?.unit_number}
      
      👤 Guest Information / معلومات النزيل:
      Name: ${selectedBookingForInvoice.tenant_name}
      Phone: ${selectedBookingForInvoice.tenant_phone}
      Email: ${selectedBookingForInvoice.tenant_email || 'Not provided'}
      
      📅 Booking Details / تفاصيل الحجز:
      Check-in: ${new Date(selectedBookingForInvoice.start_date).toLocaleDateString()}
      Check-out: ${new Date(selectedBookingForInvoice.end_date).toLocaleDateString()}
      Nights: ${Math.ceil((new Date(selectedBookingForInvoice.end_date).getTime() - new Date(selectedBookingForInvoice.start_date).getTime()) / (1000 * 60 * 60 * 24))}
      
      💰 Financial Summary / الملخص المالي:
      Rent Amount: ${selectedBookingForInvoice.rent_amount} ${selectedBookingForInvoice.currency?.symbol || 'OMR'}
      Extra Amount: ${selectedBookingForInvoice.extra_amount || 0} ${selectedBookingForInvoice.currency?.symbol || 'OMR'}
      Deposit: ${selectedBookingForInvoice.deposit_amount || 0} ${selectedBookingForInvoice.currency?.symbol || 'OMR'}
      Total Amount: ${selectedBookingForInvoice.total_amount || selectedBookingForInvoice.rent_amount + (selectedBookingForInvoice.extra_amount || 0)} ${selectedBookingForInvoice.currency?.symbol || 'OMR'}
      Paid Amount: ${selectedBookingForInvoice.paid_amount || 0} ${selectedBookingForInvoice.currency?.symbol || 'OMR'}
      Remaining: ${(selectedBookingForInvoice.total_amount || selectedBookingForInvoice.rent_amount + (selectedBookingForInvoice.extra_amount || 0)) - (selectedBookingForInvoice.paid_amount || 0)} ${selectedBookingForInvoice.currency?.symbol || 'OMR'}${paymentDetailsSection}
      
      Status: ${selectedBookingForInvoice.status.toUpperCase()}
      
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      
      Thank you for your business! / شكراً لتعاملكم معنا!
      
      Generated on: ${new Date().toLocaleString()}
    `;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Invoice - ${escapeHtml(selectedBookingForInvoice.booking_number || selectedBookingForInvoice.id)}</title>
            <style>
              body {
                font-family: 'Courier New', monospace;
                margin: 20px;
                direction: ltr;
                text-align: left;
              }
              .logo-container {
                text-align: center;
                margin-bottom: 20px;
              }
              .logo-container img {
                max-width: 200px;
                max-height: 100px;
                object-fit: contain;
              }
              .invoice-content {
                white-space: pre-wrap;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 20px;
                }
              }
            </style>
          </head>
          <body>
            ${propertyLogo ? `<div class="logo-container"><img src="${escapeHtml(propertyLogo)}" alt="Property Logo" /></div>` : ''}
            <div class="invoice-content">${escapeHtml(invoiceContent)}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      
      // Auto-print after a short delay
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }

    // Close the dialog
    handleCloseInvoiceDialog();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Handle day cell clicks in the calendar
  const handleDayCellClick = async (date: Date, propertyId: number, unitId: number) => {
    const today = startOfDay(new Date());
    const selectedDate = startOfDay(date);
    
    // Check if the selected date is in the past (only if past bookings are not allowed)
    if (!allowPastBookings && isBefore(selectedDate, today)) {
      const formattedDate = format(selectedDate, 'MMMM d, yyyy');
      setSnackbar({ 
        open: true, 
        message: t('cannot_create_booking_past_date', { date: formattedDate }), 
        severity: 'error' 
      });
      return;
    }
    
    // Calculate default end date (next day)
    const endDate = new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000);
    
    try {
      // Check for hierarchical conflicts before opening the dialog
      const conflictResult = await bookingApi.checkConflicts({
        unit_id: unitId,
        start_date: format(selectedDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      });
      
      if (conflictResult.conflict) {
        // Show conflict message and don't open the dialog
        const conflictingBooking = conflictResult.conflicting_booking;
        const conflictType = conflictResult.conflict_type;
        const unit = conflictResult.unit;
        const conflictingUnit = conflictResult.conflicting_unit;
        
        // Generate translated conflict message based on type
        let conflictMessage = '';
        switch (conflictType) {
          case 'same_unit':
            conflictMessage = t('conflict_same_unit', { unitName: unit?.name || 'Unknown Unit' });
            break;
          case 'parent_unit':
            conflictMessage = t('conflict_parent_unit', { 
              unitName: unit?.name || 'Unknown Unit', 
              conflictingUnitName: conflictingUnit?.name || 'Unknown Unit' 
            });
            break;
          case 'child_unit':
            conflictMessage = t('conflict_child_unit', { 
              unitName: unit?.name || 'Unknown Unit', 
              conflictingUnitName: conflictingUnit?.name || 'Unknown Unit' 
            });
            break;
          default:
            conflictMessage = t('conflict_default');
            break;
        }
        
        // Add conflicting booking details
        if (conflictingBooking) {
          conflictMessage += `\n\n${t('conflicting_booking_details', {
            id: conflictingBooking.id,
            tenant: conflictingBooking.tenant_name,
            startDate: conflictingBooking.start_date,
            endDate: conflictingBooking.end_date,
            unitName: conflictingBooking.unit_name
          })}`;
        }
        
        setSnackbar({ 
          open: true, 
          message: conflictMessage, 
          severity: 'error' 
        });
        return;
      }
      
    } catch (error: any) {
      console.error('Failed to check booking conflicts:', error);
      // If conflict check fails, show a warning but allow the dialog to open
            setSnackbar({
        open: true, 
        message: t('booking_conflict_check_failed'), 
        severity: 'error' 
      });
    }
    
    // Fetch the daily rate for the selected date and unit
    let dailyRate = 0;
    try {
      const rateResponse = await dailyRatesApi.getRates(
        unitId,
        format(selectedDate, 'yyyy-MM-dd'),
        format(selectedDate, 'yyyy-MM-dd')
      );
      
      // Get the rate for the specific date
      const dateKey = format(selectedDate, 'yyyy-MM-dd');
      dailyRate = rateResponse.rates[dateKey] || 0;
    } catch (error) {
      console.error('Failed to fetch daily rate:', error);
      // If fetching fails, use 0 as default
      dailyRate = 0;
    }
    
    // Pre-fill the form with selected property, unit, and date
    reset({
      property_id: propertyId.toString(),
      unit_id: unitId.toString(),
      tenant_name: '',
      tenant_email: '',
      tenant_phone: '',
      country_code: getCountryPhoneCode(getUserCountry()),
      start_date: format(selectedDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
      nights: 1,
      rent_per_night: dailyRate,
      rent_amount: dailyRate, // Set initial rent amount to daily rate (for 1 night)
      extra_amount: 0,
      extra_description: '',
      deposit_amount: 0,
      status: 'active',
      notes: '',
    });
    
    // Open the booking dialog
    setEditingBooking(null); // Ensure we're in "add" mode
    setOpenDialog(true);
  };

  // Calculate stats
  const activeBookings = bookings.filter((b: any) => b.status === 'active').length;
  const pendingBookings = bookings.filter((b: any) => b.status === 'pending').length;
  const totalRevenue = bookings
    .filter((b: any) => b.status === 'active')
    .reduce((sum: number, b: any) => sum + b.rent_amount, 0);

  // Filter bookings based on search criteria
  const filteredBookings = useMemo(() => {
    if (!bookings) return [];
    
    return bookings.filter((booking: any) => {
      // Status filter: include all statuses if no filter is selected, otherwise filter by selected status
      const matchesStatus = !filters.status || booking.status === filters.status;
      
      const matchesBookingNumber = !filters.bookingNumber || 
        booking.booking_number?.toString().includes(filters.bookingNumber) ||
        booking.id?.toString().includes(filters.bookingNumber);
      
      const matchesTenantName = !filters.tenantName || 
        booking.tenant_name?.toLowerCase().includes(filters.tenantName.toLowerCase());
      
      const matchesTenantPhone = !filters.tenantPhone || 
        booking.tenant_phone?.toLowerCase().includes(filters.tenantPhone.toLowerCase());
      
      const matchesProperty = !filters.property || 
        booking.property?.name?.toLowerCase().includes(filters.property.toLowerCase());
      
          const matchesUnit = !filters.unit ||
      booking.unit?.id?.toString() === filters.unit;
      
      const matchesCreatedBy = !filters.createdBy || 
        booking.user?.name?.toLowerCase().includes(filters.createdBy.toLowerCase()) ||
        booking.user?.email?.toLowerCase().includes(filters.createdBy.toLowerCase());
      
      // Date filtering based on start_date
      const matchesFromDate = !filters.fromDate || 
        new Date(booking.start_date) >= new Date(filters.fromDate);
      
      const matchesToDate = !filters.toDate || 
        new Date(booking.start_date) <= new Date(filters.toDate);
      
      return matchesStatus && matchesBookingNumber && matchesTenantName && matchesTenantPhone && 
             matchesProperty && matchesUnit && matchesCreatedBy && 
             matchesFromDate && matchesToDate;
    });
  }, [bookings, filters]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      bookingNumber: '',
      tenantName: '',
      tenantPhone: '',
      property: '',
      unit: '',
      createdBy: '',
      fromDate: '',
      toDate: '',
      status: ''
    });
  };

  // Force refresh bookings data
  const refreshBookings = () => {
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
  };

  // Export filtered bookings to PDF
  const exportToPDF = async () => {
    try {
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      
      // Function to detect Arabic text
      const isArabicText = (text: string) => {
        const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
        return arabicRegex.test(text);
      };
      
      // Function to create readable Arabic text placeholders
      const createArabicPlaceholder = (text: string) => {
        if (!text || !isArabicText(text)) return text;
        
        // Try to extract any English/numeric parts
        const parts = text.split(/\s+/);
        const englishParts = parts.filter(part => !isArabicText(part));
        const arabicParts = parts.filter(part => isArabicText(part));
        
        if (englishParts.length > 0) {
          // If there are English parts, use them in the placeholder
          return `[Arabic: ${englishParts.join(' ')}]`;
        } else if (arabicParts.length > 0) {
          // Pure Arabic text - create a descriptive placeholder
          const textLength = text.length;
          if (textLength <= 10) {
            return '[Arabic: Unit]';
          } else if (textLength <= 20) {
            return '[Arabic: Property]';
          } else {
            return '[Arabic: Description]';
          }
        }
        return '[Arabic Text]';
      };
      
      // Function to properly display Arabic text in PDF
      const displayArabicText = (text: string, x: number, y: number, options: { align?: 'left' | 'right' | 'center', maxWidth?: number } = {}) => {
        if (!text) {
          pdf.text('N/A', x, y);
          return;
        }
        
        try {
          if (isArabicText(text)) {
            // For Arabic text, try a more robust approach
            try {
              // First try: normalize and render the text
              const normalizedText = text.normalize('NFC');
              pdf.setFont('helvetica', 'normal');
              
              // Test if we can get text width (indicates font support)
              const testWidth = pdf.getTextWidth(normalizedText);
              
              if (testWidth > 0 && testWidth < 1000 && !isNaN(testWidth)) {
                // Font seems to support the text
                if (options.align === 'right') {
                  const adjustedX = x - testWidth;
                  pdf.text(normalizedText, adjustedX, y);
                } else if (options.align === 'center' && options.maxWidth) {
                  const adjustedX = x + (options.maxWidth - testWidth) / 2;
                  pdf.text(normalizedText, adjustedX, y);
                } else {
                  pdf.text(normalizedText, x, y);
                }
                return; // Success!
              } else {
                throw new Error('Font does not support Arabic text');
              }
            } catch (renderError) {
              // Arabic rendering failed, use readable placeholder
              const placeholder = createArabicPlaceholder(text);
              pdf.text(placeholder, x, y);
              console.warn('Arabic text rendered as placeholder:', text, '→', placeholder);
            }
          } else {
            // For non-Arabic text, use standard alignment
            if (options.align === 'center' && options.maxWidth) {
              const textWidth = pdf.getTextWidth(text);
              const adjustedX = x + (options.maxWidth - textWidth) / 2;
              pdf.text(text, adjustedX, y);
            } else {
              pdf.text(text, x, y);
            }
          }
        } catch (error) {
          console.warn('Error rendering text:', error);
          // Final fallback
          const fallbackText = isArabicText(text) ? createArabicPlaceholder(text) : text.replace(/[^\x00-\x7F]/g, '?');
          pdf.text(fallbackText, x, y);
        }
      };
      
      // Function to clean and prepare text for PDF display
      const prepareTextForPDF = (text: string) => {
        if (!text) return 'N/A';
        
        // Return the original text to be rendered properly
        return text;
      };
      
      // Function to add footer to each page
      const addFooter = (pageNum: number, totalPages: number) => {
        const footerY = pageHeight - 10;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        
        // Left side - Company/System name
        displayArabicText('Property Management System', margin, footerY);
        
        // Center - Generated date
        const centerText = `Generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')}`;
        const centerX = (pageWidth - pdf.getTextWidth(centerText)) / 2;
        displayArabicText(centerText, centerX, footerY);
        
        // Right side - Page number
        const pageText = `Page ${pageNum} of ${totalPages}`;
        const pageX = pageWidth - margin - pdf.getTextWidth(pageText);
        displayArabicText(pageText, pageX, footerY);
        
        // Add a line above the footer
        pdf.setLineWidth(0.1);
        pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      };
      
      // Add title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      displayArabicText('Bookings Report', margin, 20);
      
      // Add generation date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      displayArabicText(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, margin, 30);
      displayArabicText(`Total bookings: ${filteredBookings.length}`, margin, 35);
      
      // Add filters info if any are applied
      const activeFilters = Object.entries(filters).filter(([_, value]) => value !== '');
      if (activeFilters.length > 0) {
        displayArabicText('Applied filters:', margin, 40);
        let yPos = 45;
        activeFilters.forEach(([key, value]) => {
          const filterName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          displayArabicText(`${filterName}: ${value}`, margin + 5, yPos);
          yPos += 5;
        });
      }
      
      // Calculate totals
      const totals = {
        rent: 0,
        extra: 0,
        total: 0,
        paid: 0,
        remaining: 0
      };
      
      // Group by currency for totals
      const currencyTotals: { [key: string]: typeof totals } = {};
      
      filteredBookings.forEach((booking: any) => {
        const currency = booking.currency?.symbol || '$';
        if (!currencyTotals[currency]) {
          currencyTotals[currency] = { rent: 0, extra: 0, total: 0, paid: 0, remaining: 0 };
        }
        
        // Use display_amounts if available, otherwise fallback to raw amounts
        if (booking.display_amounts) {
          currencyTotals[currency].rent += Number(booking.display_amounts.rent_amount?.value || 0);
          currencyTotals[currency].extra += Number(booking.display_amounts.extra_amount?.value || 0);
          currencyTotals[currency].total += Number(booking.display_amounts.total_amount?.value || 0);
          currencyTotals[currency].paid += Number(booking.display_amounts.paid_amount?.value || 0);
          
          const totalAmt = Number(booking.display_amounts.total_amount?.value || 0);
          const paidAmt = Number(booking.display_amounts.paid_amount?.value || 0);
          currencyTotals[currency].remaining += Math.max(0, totalAmt - paidAmt);
        } else {
          // Fallback for old bookings
          currencyTotals[currency].rent += Number(booking.rent_amount || 0);
          currencyTotals[currency].extra += Number(booking.extra_amount || 0);
          currencyTotals[currency].total += Number(booking.total_amount || 0);
          currencyTotals[currency].paid += Number(booking.paid_amount || 0);
          
          const totalAmt = Number(booking.total_amount || 0);
          const paidAmt = Number(booking.paid_amount || 0);
          currencyTotals[currency].remaining += Math.max(0, totalAmt - paidAmt);
        }
      });
      
      // Calculate totals for all currencies combined (for single currency display)
      const grandTotals = {
        rent: 0,
        extra: 0,
        total: 0,
        paid: 0,
        remaining: 0
      };
      
      // Sum up all currencies to display a single summary
      Object.values(currencyTotals).forEach(amounts => {
        grandTotals.rent += amounts.rent;
        grandTotals.extra += amounts.extra;
        grandTotals.total += amounts.total;
        grandTotals.paid += amounts.paid;
        grandTotals.remaining += amounts.remaining;
      });
      
      // Use the main currency symbol (first currency or default)
      const mainCurrency = Object.keys(currencyTotals)[0] || '$';
      
      // Function to draw a summary card
      const drawSummaryCard = (x: number, y: number, width: number, height: number, title: string, amount: string, currency: string) => {
        // Draw card border
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.rect(x, y, width, height);
        
        // Add light background
        pdf.setFillColor(248, 249, 250);
        pdf.rect(x, y, width, height, 'F');
        
        // Redraw border on top
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(x, y, width, height);
        
        // Add title
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(100, 100, 100);
        const titleWidth = pdf.getTextWidth(title);
        const titleX = x + (width - titleWidth) / 2;
        displayArabicText(title, titleX, y + 12);
        
        // Add amount
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        const amountText = `${currency}${amount}`;
        const amountWidth = pdf.getTextWidth(amountText);
        const amountX = x + (width - amountWidth) / 2;
        displayArabicText(amountText, amountX, y + 25);
        
        // Reset colors
        pdf.setTextColor(0, 0, 0);
        pdf.setDrawColor(0, 0, 0);
      };
      
      // Start with table content first
      let currentY = activeFilters.length > 0 ? 60 + (activeFilters.length * 5) : 50;
      
      // Table headers
      const headers = ['#', 'Booking #', 'Tenant', 'Property', 'Unit', 'Check-in', 'Check-out', 'Rent', 'Extra', 'Total', 'Paid', 'Remaining', 'Status'];
      const colWidths = [12, 22, 35, 50, 30, 22, 22, 18, 18, 18, 18, 18, 18];
      
      // Draw table headers
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      let xPos = margin;
      headers.forEach((header, index) => {
        displayArabicText(header, xPos, currentY);
        xPos += colWidths[index];
      });
      currentY += 8;
      
      // Track page count for footer
      let pageCount = 1;
      
      // Draw table data
      pdf.setFont('helvetica', 'normal');
      filteredBookings.forEach((booking: any, index: number) => {
        // Check if we need a new page (leave space for footer)
        if (currentY > pageHeight - 30) {
          pageCount++;
          pdf.addPage();
          currentY = 20;
          
          // Re-draw headers on new page
          pdf.setFont('helvetica', 'bold');
          xPos = margin;
          headers.forEach((header, colIndex) => {
            displayArabicText(header, xPos, currentY);
            xPos += colWidths[colIndex];
          });
          currentY += 8;
          pdf.setFont('helvetica', 'normal');
        }
        
        xPos = margin;
        const rowData = [
          (index + 1).toString(),
          prepareTextForPDF(booking.booking_number),
          prepareTextForPDF(booking.tenant_name),
          prepareTextForPDF(booking.property?.name),
          prepareTextForPDF(booking.unit?.name),
          booking.start_date ? format(new Date(booking.start_date), 'dd/MM/yyyy') : 'N/A',
          booking.end_date ? format(new Date(booking.end_date), 'dd/MM/yyyy') : 'N/A',
          formatAmountForPDF(booking, 'rent_amount'),
          formatAmountForPDF(booking, 'extra_amount'),
          formatAmountForPDF(booking, 'total_amount'),
          formatAmountForPDF(booking, 'paid_amount'),
          formatRemainingForPDF(booking),
          prepareTextForPDF(booking.status)
        ];
        
        rowData.forEach((data, colIndex) => {
          // Intelligent truncation based on column type
          let displayText = data;
          
          // For property and unit columns, use more generous truncation
          if (colIndex === 3 || colIndex === 4) { // Property and Unit columns
            const maxLength = Math.floor(colWidths[colIndex] / 2.2); // More generous for names
            displayText = data.length > maxLength ? data.substring(0, maxLength - 3) + '...' : data;
          } else if (colIndex === 2) { // Tenant column
            const maxLength = Math.floor(colWidths[colIndex] / 2.3);
            displayText = data.length > maxLength ? data.substring(0, maxLength - 3) + '...' : data;
          } else {
            // For other columns, use standard truncation
            const maxLength = Math.floor(colWidths[colIndex] / 2.5);
            displayText = data.length > maxLength ? data.substring(0, maxLength - 3) + '...' : data;
          }
          
          // Use the improved Arabic text rendering
          try {
            if (isArabicText(displayText)) {
              // For Arabic text, we might want to align it differently
              displayArabicText(displayText, xPos, currentY, { 
                align: 'left', 
                maxWidth: colWidths[colIndex] 
              });
            } else {
              pdf.text(displayText, xPos, currentY);
            }
          } catch (error) {
            console.error('Error rendering table cell:', error);
            // Fallback to basic text rendering
            pdf.text(displayText.replace(/[^\x00-\x7F]/g, '?'), xPos, currentY);
          }
          
          xPos += colWidths[colIndex];
        });
        currentY += 6;
      });
      
      // Add summary section at the bottom
      currentY += 20; // Add some space before summary
      
      // Check if we need a new page for summary (leave space for footer + summary)
      const summaryHeight = 60; // Approximate height needed for summary
      if (currentY + summaryHeight > pageHeight - 30) {
        pageCount++;
        pdf.addPage();
        currentY = 20;
      }
      
      // Add summary header
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      displayArabicText(`Summary (${filteredBookings.length} booking${filteredBookings.length !== 1 ? 's' : ''})`, margin, currentY);
      currentY += 15;
      
      // Calculate card dimensions and positions
      const cardWidth = 55;
      const cardHeight = 35;
      const cardSpacing = 5;
      const totalWidth = pageWidth - 2 * margin;
      const availableWidth = totalWidth - 4 * cardSpacing;
      const adjustedCardWidth = availableWidth / 5; // 5 cards
      
      // Draw summary cards
      let cardX = margin;
      
      // Rent Amount Card
      drawSummaryCard(cardX, currentY, adjustedCardWidth, cardHeight, 'Rent Amount', grandTotals.rent.toLocaleString(), mainCurrency);
      cardX += adjustedCardWidth + cardSpacing;
      
      // Extra Amount Card
      drawSummaryCard(cardX, currentY, adjustedCardWidth, cardHeight, 'Extra Amount', grandTotals.extra.toLocaleString(), mainCurrency);
      cardX += adjustedCardWidth + cardSpacing;
      
      // Total Amount Card
      drawSummaryCard(cardX, currentY, adjustedCardWidth, cardHeight, 'Total Amount', grandTotals.total.toLocaleString(), mainCurrency);
      cardX += adjustedCardWidth + cardSpacing;
      
      // Paid Amount Card
      drawSummaryCard(cardX, currentY, adjustedCardWidth, cardHeight, 'Paid Amount', grandTotals.paid.toLocaleString(), mainCurrency);
      cardX += adjustedCardWidth + cardSpacing;
      
      // Remaining Amount Card
      drawSummaryCard(cardX, currentY, adjustedCardWidth, cardHeight, 'Remaining Amount', grandTotals.remaining.toLocaleString(), mainCurrency);
      
      currentY += cardHeight + 15;
      
      // If there are multiple currencies, add a detailed breakdown
      if (Object.keys(currencyTotals).length > 1) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        displayArabicText('Currency Breakdown:', margin, currentY);
        currentY += 8;
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        Object.entries(currencyTotals).forEach(([currency, amounts]) => {
          displayArabicText(`${currency} - Rent: ${amounts.rent.toLocaleString()} | Extra: ${amounts.extra.toLocaleString()} | Total: ${amounts.total.toLocaleString()} | Paid: ${amounts.paid.toLocaleString()} | Remaining: ${amounts.remaining.toLocaleString()}`, margin, currentY);
          currentY += 5;
        });
        currentY += 10;
      }
      
      // Update total pages count to include summary page if it was added
      const totalPages = pageCount;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter(i, totalPages);
      }
      
      // Save the PDF
      pdf.save(`bookings-report-${format(new Date(), 'yyyy-MM-dd-HHmm')}.pdf`);
      
      setSnackbar({
        open: true,
        message: 'PDF exported successfully!',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setSnackbar({
        open: true,
        message: 'Error exporting PDF. Please try again.',
        severity: 'error'
      });
    }
  };
  
  // Helper function to format amounts for PDF
  const formatAmountForPDF = (booking: any, amountType: string) => {
    if (!booking.display_amounts) {
      const amount = booking[amountType] || 0;
      const symbol = booking.currency?.symbol || '$';
      return `${symbol}${Number(amount).toLocaleString()}`;
    }
    
    const displayAmount = booking.display_amounts[amountType];
    if (!displayAmount) return '$0';
    
    const symbol = displayAmount.symbol || '$';
    const value = displayAmount.value || 0;
    return `${symbol}${Number(value).toLocaleString()}`;
  };
  
  // Helper function to format remaining amount for PDF
  const formatRemainingForPDF = (booking: any) => {
    if (!booking.display_amounts) {
      const total = Number(booking.total_amount || 0);
      const paid = Number(booking.paid_amount || 0);
      const remaining = Math.max(0, total - paid);
      const symbol = booking.currency?.symbol || '$';
      return `${symbol}${remaining.toLocaleString()}`;
    }
    
    const totalDisplay = booking.display_amounts.total_amount;
    const paidDisplay = booking.display_amounts.paid_amount;
    
    if (!totalDisplay || !paidDisplay) return '$0';
    
    const total = Number(totalDisplay.value || 0);
    const paid = Number(paidDisplay.value || 0);
    const remaining = Math.max(0, total - paid);
    const symbol = totalDisplay.symbol || '$';
    return `${symbol}${remaining.toLocaleString()}`;
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 0 }}>
      {/* Error Alert for Currencies */}
      {currenciesError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Failed to load currencies. Some features may not work properly. Please refresh the page or try again later.
        </Alert>
      )}
      
      
      {/* Sections Above Calendar */}
      {showSectionsAboveCalendar && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" component="h1">
              {t('bookings')}
            </Typography>
            
            <Box display="flex" alignItems="center" gap={2}>
              {/* View Toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newView) => newView && setViewMode(newView)}
                size="small"
              >
                <ToggleButton value="calendar">
                  <CalendarMonth />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewList />
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Allow Past Bookings Toggle */}
              <Tooltip title="When enabled, allows creating new bookings with start dates in the past">
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowPastBookings}
                      onChange={(e) => handleTogglePastBookings(e.target.checked)}
                      size="small"
                      color="warning"
                    />
                  }
                  label={t('allow_past_bookings')}
                  sx={{ 
                    ml: 2,
                    '& .MuiFormControlLabel-label': {
                      color: allowPastBookings ? 'warning.main' : 'text.secondary',
                      fontWeight: allowPastBookings ? 'medium' : 'normal'
                    }
                  }}
                />
              </Tooltip>
              
              {/* Refresh Button */}
              <Tooltip title={t('refresh_booking_data')}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={refreshBookings}
                    size="small"
                    disabled={bookingsLoading}
                  >
                    {bookingsLoading ? t('loading') : t('refresh')}
                  </Button>
                </span>
              </Tooltip>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenDialog(true)}
              >
                {t('add_booking')}
              </Button>
            </Box>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('total_bookings')}
              </Typography>
              <Typography variant="h5">
                {bookings.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('active_bookings')}
              </Typography>
              <Typography variant="h5">
                {activeBookings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('pending_bookings')}
              </Typography>
              <Typography variant="h5">
                {pendingBookings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('total_revenue')}
              </Typography>
              <Typography variant="h5">
                {(() => {
                  if (!Array.isArray(currencies) || currencies.length === 0) {
                    return `$${totalRevenue.toLocaleString()}`;
                  }
                  const defaultCurrency = currencies.find((c: any) => c.is_default);
                  const symbol = defaultCurrency?.symbol || '$';
                  return `${symbol}${totalRevenue.toLocaleString()}`;
                })()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        </>
      )}

      {/* Filters - Only show in list view */}
      {viewMode === 'list' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter Bookings
            </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Booking #"
                fullWidth
                variant="outlined"
                value={filters.bookingNumber}
                onChange={(e) => setFilters({ ...filters, bookingNumber: e.target.value })}
                placeholder="Search by booking number..."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Tenant Name"
                fullWidth
                variant="outlined"
                value={filters.tenantName}
                onChange={(e) => setFilters({ ...filters, tenantName: e.target.value })}
                placeholder="Search by tenant name..."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Tenant Phone"
                fullWidth
                variant="outlined"
                value={filters.tenantPhone}
                onChange={(e) => setFilters({ ...filters, tenantPhone: e.target.value })}
                placeholder="Search by phone number..."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Property</InputLabel>
                <Select
                  value={filters.property}
                  onChange={(e) => setFilters({ ...filters, property: e.target.value, unit: '' })}
                  label="Property"
                >
                  <MenuItem value="">All Properties</MenuItem>
                  {properties.map((property: any) => (
                    <MenuItem key={property.id} value={property.name}>
                      {property.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <HierarchicalUnitSelector
                units={allUnits}
                selectedPropertyId={(() => {
                  // Find property ID from property name in filters
                  const selectedProperty = (properties as any[]).find(p => p.name === filters.property);
                  return selectedProperty ? selectedProperty.id : '';
                })()}
                value={filters.unit}
                onChange={(value) => setFilters({ ...filters, unit: value })}
                label="Unit"
                disabled={!filters.property}
                showAllOption={true}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="From Date"
                type="date"
                fullWidth
                variant="outlined"
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Start date from"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="To Date"
                type="date"
                fullWidth
                variant="outlined"
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Start date to"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Created By</InputLabel>
                <Select
                  value={filters.createdBy}
                  onChange={(e) => setFilters({ ...filters, createdBy: e.target.value })}
                  label="Created By"
                >
                  <MenuItem value="">All Users</MenuItem>
                  {users.map((user: any) => (
                    <MenuItem key={user.id} value={user.name}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<Clear />}
                  color="secondary"
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}

      {/* Main Content */}
      {viewMode === 'calendar' ? (
        <BookingCalendar
          bookings={bookings}
          properties={properties as any}
          units={allUnits}
          onDayCellClick={handleDayCellClick}
          onEditBooking={handleEdit}
          onViewBooking={(booking) => {
          }}
          onRefresh={() => queryClient.invalidateQueries({ queryKey: ['bookings'] })}
          showSectionsAboveCalendar={showSectionsAboveCalendar}
          sidebarCollapsed={sidebarCollapsed}
          drawerWidth={drawerWidth}
        />
      ) : (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                All Bookings
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="outlined"
                  onClick={exportToPDF}
                  startIcon={<Download />}
                  size="small"
                  disabled={filteredBookings.length === 0}
                >
                  Export PDF
                </Button>
                <Typography variant="body2" color="textSecondary">
                  Showing {filteredBookings.length} of {bookings.length} bookings
                </Typography>
              </Box>
            </Box>
            
            {bookingsLoading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : filteredBookings.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Booking #</TableCell>
                      <TableCell>{t('property')}</TableCell>
                      <TableCell>{t('unit')}</TableCell>
                      <TableCell>{t('tenant')}</TableCell>
                      <TableCell>{t('contact')}</TableCell>
                      <TableCell>{t('start_date')}</TableCell>
                      <TableCell>{t('end_date')}</TableCell>
                      <TableCell>{t('rent')}</TableCell>
                      <TableCell>Extra Amount</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Remaining</TableCell>
                      <TableCell>{t('status')}</TableCell>
                      <TableCell>{t('created_by')}</TableCell>
                      <TableCell>{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBookings.map((booking: any) => (
                      <TableRow 
                        key={booking.id}
                        sx={{
                          opacity: booking.status === 'cancelled' ? 0.6 : 1,
                          backgroundColor: booking.status === 'cancelled' ? 'rgba(244, 67, 54, 0.04)' : 'inherit',
                          '&:hover': {
                            backgroundColor: booking.status === 'cancelled' ? 'rgba(244, 67, 54, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            #{booking.booking_number || booking.id}
                          </Typography>
                        </TableCell>
                        <TableCell>{booking.property?.name}</TableCell>
                        <TableCell>{booking.unit?.unit_number}</TableCell>
                        <TableCell>{booking.tenant_name}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {booking.tenant_email || 'No email provided'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {booking.tenant_phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatDate(booking.start_date)}
                        </TableCell>
                        <TableCell>
                          {formatDate(booking.end_date)}
                        </TableCell>
                        <TableCell>
                          {formatAmountWithCurrency(booking, 'rent_amount')}
                        </TableCell>
                        <TableCell>
                          {formatAmountWithCurrency(booking, 'extra_amount')}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {formatAmountWithCurrency(booking, 'total_amount')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={(() => {
                              const remaining = Number(booking.total_amount || 0) - Number(booking.paid_amount || 0);
                              return remaining > 0 ? 'error' : 'success';
                            })()}
                            fontWeight="medium"
                          >
                            {formatRemainingAmount(booking)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.status}
                            color={getStatusColor(booking.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {booking.user?.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {booking.user?.email || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleView(booking)}
                            color="info"
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleInvoice(booking)}
                            color="secondary"
                            title="Generate Invoice"
                          >
                            <Receipt />
                          </IconButton>
                          {booking.status === 'pending' && (
                            <IconButton
                              size="small"
                              onClick={() => handleConfirm(booking)}
                              color="success"
                              title="Confirm Booking"
                            >
                              <CheckCircle />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => { handleEdit(booking)}}
                            color="primary"
                            disabled={booking.status === 'cancelled'}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(booking.id)}
                            color="error"
                            disabled={booking.status === 'cancelled'}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {bookings.length === 0 ? 'No bookings found' : 'No bookings match your filters'}
                </Typography>
                {bookings.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    startIcon={<Clear />}
                    sx={{ mt: 2 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            )}

            {/* Totals Footer - Only show when there are bookings */}
            {filteredBookings.length > 0 && (
              <>
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                    Summary ({filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''})
                  </Typography>
                  <Grid container spacing={3}>
                    {(() => {
                      // Calculate totals by currency
                      const totals = filteredBookings.reduce((acc, booking) => {
                        // Get currency symbol
                        let symbol = '$';
                        if (booking.display_amounts?.total_amount?.symbol) {
                          symbol = booking.display_amounts.total_amount.symbol;
                        } else if (booking.currency?.symbol) {
                          symbol = booking.currency.symbol;
                        }

                        if (!acc[symbol]) {
                          acc[symbol] = { rent: 0, extra: 0, total: 0, paid: 0, remaining: 0 };
                        }

                        // Rent amount
                        let rentAmount = 0;
                        if (booking.display_amounts?.rent_amount?.value) {
                          rentAmount = Number(booking.display_amounts.rent_amount.value);
                        } else {
                          rentAmount = Number(booking.rent_amount || 0);
                        }

                        // Extra amount
                        let extraAmount = 0;
                        if (booking.display_amounts?.extra_amount?.value) {
                          extraAmount = Number(booking.display_amounts.extra_amount.value);
                        } else {
                          extraAmount = Number(booking.extra_amount || 0);
                        }

                        // Total amount
                        let totalAmount = 0;
                        if (booking.display_amounts?.total_amount?.value) {
                          totalAmount = Number(booking.display_amounts.total_amount.value);
                        } else {
                          totalAmount = Number(booking.total_amount || 0);
                        }

                        // Paid amount
                        let paidAmount = 0;
                        if (booking.display_amounts?.paid_amount?.value) {
                          paidAmount = Number(booking.display_amounts.paid_amount.value);
                        } else {
                          paidAmount = Number(booking.paid_amount || 0);
                        }

                        // Remaining amount
                        const remainingAmount = Math.max(0, totalAmount - paidAmount);

                        acc[symbol].rent += rentAmount;
                        acc[symbol].extra += extraAmount;
                        acc[symbol].total += totalAmount;
                        acc[symbol].paid += paidAmount;
                        acc[symbol].remaining += remainingAmount;

                        return acc;
                      }, {} as Record<string, { rent: number; extra: number; total: number; paid: number; remaining: number }>);

                      const currencyEntries = Object.entries(totals);
                      
                      return currencyEntries.map(([currency, amounts]) => (
                        <React.Fragment key={currency}>
                          {/* First Row: Rent, Extra, Total */}
                          <Grid item xs={12} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'primary.50', 
                              borderRadius: 2, 
                              border: '1px solid', 
                              borderColor: 'primary.200',
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color="primary.600" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Rent Amount{currencyEntries.length > 1 ? ` (${currency})` : ''}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.700' }}>
                                {currency}{amounts.rent.toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'secondary.50', 
                              borderRadius: 2, 
                              border: '1px solid', 
                              borderColor: 'secondary.200',
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Extra Amount{currencyEntries.length > 1 ? ` (${currency})` : ''}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.700' }}>
                                {currency}{amounts.extra.toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'success.50', 
                              borderRadius: 2, 
                              border: '1px solid', 
                              borderColor: 'success.200',
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color="success.600" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Total Amount{currencyEntries.length > 1 ? ` (${currency})` : ''}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.700' }}>
                                {currency}{amounts.total.toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          {/* Second Row: Paid and Remaining */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'info.50', 
                              borderRadius: 2, 
                              border: '1px solid', 
                              borderColor: 'info.200',
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color="info.600" sx={{ mb: 0.5, fontWeight: 500 }}>
                                Paid Amount{currencyEntries.length > 1 ? ` (${currency})` : ''}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.700' }}>
                                {currency}{amounts.paid.toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: amounts.remaining > 0 ? 'warning.50' : 'success.50', 
                              borderRadius: 2, 
                              border: '1px solid', 
                              borderColor: amounts.remaining > 0 ? 'warning.200' : 'success.200',
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color={amounts.remaining > 0 ? 'warning.600' : 'success.600'} sx={{ mb: 0.5, fontWeight: 500 }}>
                                Remaining Amount{currencyEntries.length > 1 ? ` (${currency})` : ''}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, color: amounts.remaining >  0 ? 'warning.700' : 'success.700' }}>
                                {currency}{amounts.remaining.toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                        </React.Fragment>
                      ));
                    })()}
                  </Grid>
                  
                  {/* Additional summary info */}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                      💡 Summary shows totals for {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''} 
                      {Object.keys(filters).some(key => filters[key as keyof typeof filters]) ? ' matching current filters' : ' (all bookings)'}
                      {(() => {
                        const currencyCount = Object.keys(
                          filteredBookings.reduce((acc, booking) => {
                            const symbol = booking.display_amounts?.total_amount?.symbol || booking.currency?.symbol || '$';
                            acc[symbol] = true;
                            return acc;
                          }, {} as Record<string, boolean>)
                        ).length;
                        return currencyCount > 1 ? ` across ${currencyCount} currencies` : '';
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        key={editingBooking ? `edit-${editingBooking.id}` : 'new'}
      >
        <DialogTitle>
          {editingBooking ? 'Edit Booking' : 'Add New Booking'}
        </DialogTitle>
        <DialogContent>
          {/* Validation Summary */}
          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Please fix the following errors:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field}>
                    <Typography variant="body2">
                      {error?.message || `${field} is invalid`}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          
          {/* Past Bookings Alert */}
          {allowPastBookings && !editingBooking && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                📅 Past booking creation is enabled. You can select dates in the past for new bookings.
              </Typography>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit(onSubmitBooking)}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.property_id}>
                  <InputLabel>Property *</InputLabel>
                  <Controller
                    name="property_id"
                    control={control}
                    rules={{ 
                      required: 'Property is required',
                      validate: (value) => value !== '' || 'Please select a property'
                    }}
                    render={({ field }) => (
                      <Select {...field} label="Property *">
                        {properties.map((property: any) => (
                          <MenuItem key={property.id} value={property.id.toString()}>
                            {property.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.property_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.property_id.message}
                    </Typography>
                  )}
                </FormControl>
                           </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="unit_id"
                  control={control}
                  rules={{ 
                    required: 'Unit is required',
                    validate: (value) => value !== '' || 'Please select a unit'
                  }}
                  render={({ field }) => (
                    <HierarchicalUnitSelector
                      units={allUnits}
                      selectedPropertyId={watchPropertyId}
                      value={field.value}
                      onChange={field.onChange}
                      label="Unit *"
                      disabled={!watchPropertyId}
                      error={!!errors.unit_id}
                      helperText={errors.unit_id?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Broker (Optional)</InputLabel>
                  <Controller
                    name="broker_id"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Broker (Optional)">
                        <MenuItem value="">
                          <em>No broker</em>
                        </MenuItem>
                        {brokers.map((broker: any) => (
                          <MenuItem key={broker.id} value={broker.id.toString()}>
                            {broker.name} {broker.company_name && `(${broker.company_name})`}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="tenant_name"
                  control={control}
                  rules={{ 
                    required: 'Tenant name is required',
                    minLength: {
                      value: 2,
                      message: 'Tenant name must be at least 2 characters'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Tenant Name *"
                      fullWidth
                      error={!!errors.tenant_name}
                      helperText={errors.tenant_name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="tenant_email"
                  control={control}
                  rules={{ 
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address'
                    },
                    validate: (value) => {
                      // Email is optional, but if provided, must be valid
                      return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Please enter a valid email address';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Tenant Email"
                      type="email"
                      fullWidth
                      error={!!errors.tenant_email}
                      helperText={errors.tenant_email?.message}
                      placeholder="optional@example.com"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name="country_code"
                  control={control}
                  render={({ field }) => (
                    <CountryCodeSelector
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.country_code}
                      helperText={errors.country_code?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="tenant_phone"
                  control={control}
                  rules={{
                    required: 'Phone number is required',
                    pattern: {
                      value: /^[\d\s\-()]+$/,
                      message: 'Please enter a valid phone number (numbers, spaces, hyphens, parentheses only)'
                    },
                    minLength: {
                      value: 7,
                      message: 'Phone number must be at least 7 digits'
                    },
                    maxLength: {
                      value: 15,
                      message: 'Phone number cannot exceed 15 digits'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone Number *"
                      fullWidth
                      error={!!errors.tenant_phone}
                      helperText={errors.tenant_phone?.message}
                      placeholder="1234567890"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Status">
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Auto-cancel selector - only show for pending bookings */}
              {watchStatus === 'pending' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Auto-cancel after</InputLabel>
                    <Controller
                      name="cancel_after_minutes"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label="Auto-cancel after">
                          {cancelAfterOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <Controller
                  name="start_date"
                  control={control}
                  rules={{ 
                    required: 'Start date is required',
                    validate: (value) => {
                      // Allow past dates when editing existing bookings
                      if (editingBooking) return true;
                      // Allow past dates if the setting is enabled
                      if (allowPastBookings) return true;
                      const today = new Date().toISOString().split('T')[0];
                      return value >= today || 'Start date cannot be in the past';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Start Date *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.start_date}
                      helperText={
                        errors.start_date?.message || 
                        (allowPastBookings && !editingBooking 
                          ? "Past dates are allowed" 
                          : editingBooking 
                            ? "Date can be modified" 
                            : "Date cannot be in the past"
                        )
                      }
                      onChange={(e) => {
                        field.onChange(e);
                        const newStartDate = e.target.value;
                        const currentNights = watchNights;
                        if (newStartDate && currentNights > 0) {
                          const newEndDate = calculateEndDate(newStartDate, currentNights);
                          setValue('end_date', newEndDate, { shouldValidate: true });
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="nights"
                  control={control}
                  rules={{ 
                    required: 'Number of nights is required',
                    min: {
                      value: 1,
                      message: 'Must be at least 1 night'
                    },
                    validate: (value) => {
                      const numValue = parseInt(String(value));
                      return (!isNaN(numValue) && numValue > 0) || 'Please enter a valid number of nights';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Number of Nights *"
                      type="number"
                      fullWidth
                      inputProps={{ min: 1 }}
                      error={!!errors.nights}
                      helperText={errors.nights?.message}
                      onChange={(e) => {
                        field.onChange(e);
                        const newNights = parseInt(e.target.value);
                        const currentStartDate = watchStartDate;
                        const currentRentPerNight = watchRentPerNight;
                        if (currentStartDate && newNights > 0) {
                          const newEndDate = calculateEndDate(currentStartDate, newNights);
                          setValue('end_date', newEndDate, { shouldValidate: true });
                        }
                        if (newNights > 0 && currentRentPerNight > 0) {
                          const totalRent = calculateTotalRent(currentRentPerNight, newNights);
                          setValue('rent_amount', totalRent, { shouldValidate: true });
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="end_date"
                  control={control}
                  rules={{ 
                    required: 'End date is required',
                    validate: (value, formValues) => {
                      return value > formValues.start_date || 'End date must be after start date';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="End Date *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.end_date}
                      helperText={errors.end_date?.message}
                      onChange={(e) => {
                        field.onChange(e);
                        const newEndDate = e.target.value;
                        const currentStartDate = watchStartDate;
                        if (currentStartDate && newEndDate) {
                          const newNights = calculateNights(currentStartDate, newEndDate);
                          setValue('nights', newNights, { shouldValidate: true });
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="rent_per_night"
                  control={control}
                  rules={{
                    required: 'Rent per night is required',
                    min: {
                      value: 0,
                      message: 'Rent per night cannot be negative'
                    },
                    validate: (value) => {
                      const numValue = parseFloat(String(value));
                      return !isNaN(numValue) || 'Please enter a valid number';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rent per Night *"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      error={!!errors.rent_per_night}
                      helperText={errors.rent_per_night?.message}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{getPropertyCurrencySymbol()}</Typography>
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        const newRentPerNight = parseFloat(e.target.value) || 0;
                        const currentNights = watchNights;
                        if (newRentPerNight >= 0 && currentNights > 0) {
                          const totalRent = calculateTotalRent(newRentPerNight, currentNights);
                          setValue('rent_amount', totalRent, { shouldValidate: true });
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="rent_amount"
                  control={control}
                  rules={{
                    required: 'Rent amount is required',
                    min: {
                      value: 0,
                      message: 'Rent amount cannot be negative'
                    },
                    validate: (value) => {
                      const numValue = parseFloat(String(value));
                      return !isNaN(numValue) || 'Please enter a valid number';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rent Amount *"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      error={!!errors.rent_amount}
                      helperText={errors.rent_amount?.message}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{getPropertyCurrencySymbol()}</Typography>
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        const newRentAmount = parseFloat(e.target.value) || 0;
                        const currentNights = watchNights;
                        if (newRentAmount >= 0 && currentNights > 0) {
                          const rentPerNight = calculateRentPerNight(newRentAmount, currentNights);
                          setValue('rent_per_night', rentPerNight, { shouldValidate: true });
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Tax Display */}
              <Grid item xs={12} md={4}>
                <TextField
                  label={`Tax (${(() => {
                    const selectedUnit = allUnits.find((unit: any) => unit.id.toString() === watch('unit_id'));
                    const taxRate = selectedUnit?.tax_rate || 0;
                    return `${taxRate}%`;
                  })()})`}
                  value={(() => {
                    const rentAmount = Number(watch('rent_amount')) || 0;
                    const selectedUnit = allUnits.find((unit: any) => unit.id.toString() === watch('unit_id'));
                    const taxRate = selectedUnit?.tax_rate || 0;
                    const taxAmount = (rentAmount * taxRate) / 100;
                    return taxAmount.toFixed(2);
                  })()}
                  disabled
                  fullWidth
                  InputProps={{
                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{getPropertyCurrencySymbol()}</Typography>
                  }}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: theme => theme.palette.text.primary,
                    },
                  }}
                />
              </Grid>

              {/* Commission Fields */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="commission_rate"
                  control={control}
                  rules={{
                    min: {
                      value: 0,
                      message: 'Commission rate cannot be negative'
                    },
                    max: {
                      value: 100,
                      message: 'Commission rate cannot exceed 100%'
                    },
                    validate: (value) => {
                      const numValue = parseFloat(String(value || 0));
                      return !isNaN(numValue) || 'Please enter a valid number';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Commission Rate (%)"
                      placeholder="Enter commission rate"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      error={!!errors.commission_rate}
                      helperText={errors.commission_rate?.message || (watchBrokerId ? "Auto-filled from broker's default rate" : "")}
                      InputLabelProps={{
                        shrink: !!field.value || field.value === 0, // Always shrink when there's a value
                      }}
                      onChange={(e) => {
                        const rate = parseFloat(e.target.value) || 0;
                        const rentAmount = Number(watch('rent_amount')) || 0;
                        const extraAmount = Number(watch('extra_amount')) || 0;
                        const totalAmount = rentAmount + extraAmount;
                        const commissionAmount = (totalAmount * rate) / 100;
                        field.onChange(e);
                        setValue('commission_amount', commissionAmount);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="commission_amount"
                  control={control}
                  rules={{
                    min: {
                      value: 0,
                      message: 'Commission amount cannot be negative'
                    },
                    validate: (value) => {
                      const numValue = parseFloat(String(value || 0));
                      return !isNaN(numValue) || 'Please enter a valid number';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Commission Amount"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      error={!!errors.commission_amount}
                      helperText={errors.commission_amount?.message || "Auto-calculated from rate and total amount"}
                      InputLabelProps={{
                        shrink: !!field.value || field.value === 0, // Always shrink when there's a value
                      }}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{getPropertyCurrencySymbol()}</Typography>
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="deposit_amount"
                  control={control}
                  rules={{
                    required: 'Deposit amount is required',
                    min: {
                      value: 0,
                      message: 'Deposit amount cannot be negative'
                    },
                    validate: (value) => {
                      const numValue = parseFloat(String(value));
                      return !isNaN(numValue) || 'Please enter a valid number';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Deposit Amount *"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      error={!!errors.deposit_amount}
                      helperText={errors.deposit_amount?.message}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{getPropertyCurrencySymbol()}</Typography>
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Controller
                  name="extra_amount"
                  control={control}
                  rules={{
                    min: {
                      value: 0,
                      message: 'Extra amount cannot be negative'
                    },
                    validate: (value) => {
                      const numValue = parseFloat(String(value || 0));
                      return !isNaN(numValue) || 'Please enter a valid number';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Extra Amount"
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      error={!!errors.extra_amount}
                      helperText={errors.extra_amount?.message}
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>{getPropertyCurrencySymbol()}</Typography>
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label={`Remaining Amount: ${getPropertyCurrencySymbol()}${(() => {
                    const rentAmount = Number(watch('rent_amount')) || 0;
                    const extraAmount = Number(watch('extra_amount')) || 0;
                    const selectedUnit = allUnits.find((unit: any) => unit.id.toString() === watch('unit_id'));
                    const taxRate = selectedUnit?.tax_rate || 0;
                    const taxAmount = (rentAmount * taxRate) / 100;
                    const depositAmount = Number(watch('deposit_amount')) || 0;
                    const totalAmount = rentAmount + extraAmount + taxAmount;
                    const remainingAmount = totalAmount - depositAmount;
                    return remainingAmount.toFixed(2);
                  })()}`}
                  disabled
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: theme => theme.palette.text.primary,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="extra_description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Extra Amount Description"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="e.g., Cleaning fee, Utility charges, Damages, etc."
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmitBooking)}
            variant="contained"
            disabled={
              createBookingMutation.isPending || 
              updateBookingMutation.isPending || 
              Object.keys(errors).length > 0
            }
          >
            {createBookingMutation.isPending || updateBookingMutation.isPending ? (
              'Processing...'
            ) : (
              editingBooking ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Booking Dialog - Comprehensive */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('booking_details')} #{viewingBooking?.booking_number || viewingBooking?.id}
        </DialogTitle>
        <DialogContent>
          {viewingBooking && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('guest_name')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingBooking.tenant_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('phone_number')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingBooking.tenant_phone || 'No phone provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('email')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingBooking.tenant_email || 'No email provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('property')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {viewingBooking.property?.name} - {getUnitHierarchyNameById(viewingBooking.unit_id)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('check_in')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(parseISO(viewingBooking.start_date), 'PPP')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('check_out')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(parseISO(viewingBooking.end_date), 'PPP')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('status')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={t(viewingBooking.status)}
                    color={
                      viewingBooking.status === 'active' ? 'success' :
                      viewingBooking.status === 'pending' ? 'warning' :
                      viewingBooking.status === 'completed' ? 'info' : 'default'
                    }
                  />
                  {viewingBooking.status === 'pending' && viewingBooking.auto_cancel_at && (
                    <Typography variant="body2" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      fontWeight: 'medium',
                      color: (() => {
                        const now = new Date();
                        const cancelTime = new Date(viewingBooking.auto_cancel_at);
                        const timeDiff = cancelTime.getTime() - now.getTime();
                        return timeDiff <= 0 ? 'error.main' : 'warning.main';
                      })()
                    }}>
                      <AccessTime sx={{ fontSize: 16 }} />
                      {(() => {
                        const now = new Date();
                        const cancelTime = new Date(viewingBooking.auto_cancel_at);
                        const timeDiff = cancelTime.getTime() - now.getTime();
                        
                        if (timeDiff <= 0) {
                          return 'Auto-cancel expired';
                        }
                        
                        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
                        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                        
                        if (hours > 0) {
                          return `${hours}h ${minutes}m until auto-cancel`;
                        } else {
                          return `${minutes}m until auto-cancel`;
                        }
                      })()}
                    </Typography>
                  )}

                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('rent_amount')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(viewingBooking, 'rent_amount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Deposit Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(viewingBooking, 'deposit_amount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Extra Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(viewingBooking, 'extra_amount')}
                </Typography>
              </Grid>
              {viewingBooking.extra_description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Extra Amount Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingBooking.extra_description}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Number of Nights
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {(() => {
                    const start = parseISO(viewingBooking.start_date);
                    const end = parseISO(viewingBooking.end_date);
                    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                    return nights;
                  })()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Rent per Night
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {(() => {
                    const start = parseISO(viewingBooking.start_date);
                    const end = parseISO(viewingBooking.end_date);
                    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                    const rentAmount = Number(viewingBooking.rent_amount || 0);
                    const rentPerNight = nights > 0 ? (rentAmount / nights) : 0;
                    
                    // Currency hierarchy: booking.currency > property.currency > default
                    let symbol = '$'; // Default fallback
                    
                    if ((viewingBooking as any).currency?.symbol) {
                      symbol = (viewingBooking as any).currency.symbol;
                    } else if ((viewingBooking as any).property?.currency?.symbol) {
                      symbol = (viewingBooking as any).property.currency.symbol;
                    } else {
                      symbol = getMainCurrencySymbol();
                    }
                    
                    return `${symbol}${rentPerNight.toFixed(2)}`;
                  })()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatAmountWithCurrency(viewingBooking, 'total_amount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Remaining Amount
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatRemainingAmount(viewingBooking)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Payment Status
                </Typography>
                <Chip
                  label={viewingBooking.payment_status || 'none'}
                  color={
                    viewingBooking.payment_status === 'full' ? 'success' :
                    viewingBooking.payment_status === 'partial' ? 'warning' : 'default'
                  }
                />
              </Grid>
              {viewingBooking.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('notes')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingBooking.notes}
                  </Typography>
                </Grid>
              )}
              
              {/* Booking History Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assessment />
                  Booking History
                </Typography>
                
                {loadingHistory ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading history...
                  </Typography>
                ) : bookingHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No history available
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    {bookingHistory.map((history, index) => (
                      <Box
                        key={history.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          py: 1.5,
                          px: 2,
                          backgroundColor: index % 2 === 0 ? 'background.default' : 'background.paper',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 
                              history.action === 'created' ? 'success.main' :
                              history.action === 'updated' ? 'info.main' :
                              history.action === 'deleted' ? 'error.main' : 'grey.500',
                            mt: 0.75,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {history.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(parseISO(history.created_at), 'PPpp')} by {history.user?.name || 'Unknown User'}
                          </Typography>
                          {history.action === 'updated' && history.old_values && history.new_values && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Changes made:
                              </Typography>
                              {Object.keys(history.new_values).map((key) => {
                                const oldValue = history.old_values?.[key];
                                const newValue = history.new_values?.[key];
                                if (oldValue !== newValue) {
                                  return (
                                    <Typography key={key} variant="caption" display="block" sx={{ ml: 1 }}>
                                      • {getFieldDisplayName(key)}: {String(oldValue)} → {String(newValue)}
                                    </Typography>
                                  );
                                }
                                return null;
                              })}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>
            {t('close')}
          </Button>
          {viewingBooking && viewingBooking.status === 'pending' && (
            <Button
              startIcon={<CheckCircle />}
              onClick={async () => {
                try {
                  await bookingApi.confirm(viewingBooking.id);
                  // Refresh the bookings list
                  queryClient.invalidateQueries({ queryKey: ['bookings'] });
                  handleCloseViewDialog();
                } catch (error) {
                  console.error('Failed to confirm booking:', error);
                }
              }}
              color="success"
              variant="contained"
            >
              Confirm Booking
            </Button>
          )}
          {viewingBooking && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {
                handleEdit(viewingBooking);
                handleCloseViewDialog();
              }}
            >
              {t('edit')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Invoice Options Dialog */}
      <Dialog 
        open={openInvoiceDialog} 
        onClose={handleCloseInvoiceDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Generate Invoice - #{selectedBookingForInvoice?.booking_number || selectedBookingForInvoice?.id}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'medium' }}>
                Select Invoice Type
              </FormLabel>
              <RadioGroup
                value={invoiceType}
                onChange={(e) => setInvoiceType(e.target.value as 'summary' | 'details')}
              >
                <FormControlLabel
                  value="summary"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Financial Summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Basic financial overview with totals only
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="details"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Financial Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Includes payment history with dates for each payment
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvoiceDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleGenerateInvoice} 
            variant="contained"
            startIcon={<Receipt />}
          >
            Generate Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BookingsPage;